type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;

function rowsFromRawResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function assertPlatformAnalyticsSchemaReady(params: { rawSql: RawSql }): Promise<void> {
  const rows = rowsFromRawResult<{ table_name?: string; column_name?: string; is_nullable?: string }>(
    await params.rawSql(
      `SELECT table_name, column_name, is_nullable
       FROM information_schema.columns
       WHERE table_name IN (
        'analytics_sites',
        'analytics_events_raw',
        'analytics_daily_visitors',
        'analytics_daily_sessions'
       )`,
    ),
  );
  const columns = new Map(rows.map((row) => [`${row.table_name}.${row.column_name}`, row.is_nullable ?? ""]));
  const requireColumn = (table: string, column: string) => {
    if (!columns.has(`${table}.${column}`)) {
      throw new Error(`analytics schema missing required column: ${table}.${column}`);
    }
  };
  const requireNullable = (table: string, column: string) => {
    requireColumn(table, column);
    if (columns.get(`${table}.${column}`) !== "YES") {
      throw new Error(`analytics schema column must be nullable: ${table}.${column}`);
    }
  };

  requireColumn("analytics_sites", "scope");
  requireNullable("analytics_sites", "app_id");
  requireNullable("analytics_events_raw", "app_id");
  requireColumn("analytics_daily_visitors", "visitor_key");
  requireColumn("analytics_daily_sessions", "session_key");
}

export async function ensurePlatformAnalyticsSchema(params: { rawSql: RawSql }): Promise<void> {
  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_sites (
        id SERIAL PRIMARY KEY,
        scope TEXT NOT NULL DEFAULT 'hosted_app',
        app_id INTEGER,
        site_key TEXT NOT NULL UNIQUE,
        backend TEXT NOT NULL DEFAULT 'disabled',
        enabled BOOLEAN NOT NULL DEFAULT false,
        primary_domain TEXT,
        custom_domain TEXT,
        last_event_at TIMESTAMP,
        metadata_json TEXT NOT NULL DEFAULT '{}',
        created_at TIMESTAMP DEFAULT NOW() NOT NULL,
        updated_at TIMESTAMP DEFAULT NOW() NOT NULL
    )`);
  await params.rawSql(`ALTER TABLE analytics_sites ADD COLUMN IF NOT EXISTS scope TEXT NOT NULL DEFAULT 'hosted_app'`);
  await params.rawSql(`ALTER TABLE analytics_sites ALTER COLUMN app_id DROP NOT NULL`);
  await params.rawSql(
    `CREATE UNIQUE INDEX IF NOT EXISTS analytics_sites_app_id_unique ON analytics_sites (app_id)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_sites_app_updated_idx ON analytics_sites (app_id, updated_at DESC)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_sites_scope_updated_idx ON analytics_sites (scope, updated_at DESC)`,
  );
  await params.rawSql(`CREATE INDEX IF NOT EXISTS analytics_sites_enabled_idx ON analytics_sites (enabled)`);

  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_events_raw (
        id SERIAL PRIMARY KEY,
        site_id INTEGER NOT NULL,
        app_id INTEGER,
        schema_version INTEGER NOT NULL DEFAULT 1,
        event_name TEXT NOT NULL,
        day DATE NOT NULL,
        occurred_at TIMESTAMP DEFAULT NOW() NOT NULL,
        received_at TIMESTAMP DEFAULT NOW() NOT NULL,
        url TEXT NOT NULL,
        path TEXT NOT NULL,
        referrer TEXT,
        source TEXT NOT NULL DEFAULT 'Direct',
        country TEXT NOT NULL DEFAULT 'Unknown',
        device TEXT NOT NULL DEFAULT 'Unknown',
        browser TEXT NOT NULL DEFAULT 'Unknown',
        os TEXT NOT NULL DEFAULT 'Unknown',
        language TEXT,
        screen_width_bucket TEXT,
        screen_height_bucket TEXT,
        deploy_id TEXT,
        visitor_key TEXT NOT NULL,
        session_key TEXT NOT NULL,
        user_agent_hash TEXT,
        metadata_json TEXT NOT NULL DEFAULT '{}'
    )`);
  await params.rawSql(`ALTER TABLE analytics_events_raw ALTER COLUMN app_id DROP NOT NULL`);
  await params.rawSql(`CREATE INDEX IF NOT EXISTS analytics_events_site_day_idx ON analytics_events_raw (site_id, day)`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_events_site_occurred_idx ON analytics_events_raw (site_id, occurred_at)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_events_site_received_idx ON analytics_events_raw (site_id, received_at DESC)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_events_site_visitor_idx ON analytics_events_raw (site_id, day, visitor_key)`,
  );
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_events_site_session_idx ON analytics_events_raw (site_id, day, session_key)`,
  );

  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_daily_rollups (
        site_id INTEGER NOT NULL,
        day DATE NOT NULL,
        visitors INTEGER NOT NULL DEFAULT 0,
        visits INTEGER NOT NULL DEFAULT 0,
        pageviews INTEGER NOT NULL DEFAULT 0,
        created_at TIMESTAMP DEFAULT NOW() NOT NULL,
        updated_at TIMESTAMP DEFAULT NOW() NOT NULL,
        CONSTRAINT analytics_daily_rollups_site_day_unique UNIQUE (site_id, day)
    )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_daily_rollups_site_day_idx ON analytics_daily_rollups (site_id, day)`,
  );

  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_daily_visitors (
        site_id INTEGER NOT NULL,
        day DATE NOT NULL,
        visitor_key TEXT NOT NULL,
        created_at TIMESTAMP DEFAULT NOW() NOT NULL,
        CONSTRAINT analytics_daily_visitors_site_day_key_unique UNIQUE (site_id, day, visitor_key)
    )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_daily_visitors_site_day_idx ON analytics_daily_visitors (site_id, day)`,
  );
  await params.rawSql(
    `INSERT INTO analytics_daily_visitors (site_id, day, visitor_key, created_at)
     SELECT DISTINCT site_id, day, visitor_key, NOW()
     FROM analytics_events_raw
     WHERE visitor_key IS NOT NULL
     ON CONFLICT (site_id, day, visitor_key) DO NOTHING`,
  );

  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_daily_sessions (
        site_id INTEGER NOT NULL,
        day DATE NOT NULL,
        session_key TEXT NOT NULL,
        created_at TIMESTAMP DEFAULT NOW() NOT NULL,
        CONSTRAINT analytics_daily_sessions_site_day_key_unique UNIQUE (site_id, day, session_key)
    )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_daily_sessions_site_day_idx ON analytics_daily_sessions (site_id, day)`,
  );
  await params.rawSql(
    `INSERT INTO analytics_daily_sessions (site_id, day, session_key, created_at)
     SELECT DISTINCT site_id, day, session_key, NOW()
     FROM analytics_events_raw
     WHERE session_key IS NOT NULL
     ON CONFLICT (site_id, day, session_key) DO NOTHING`,
  );

  await params.rawSql(`CREATE TABLE IF NOT EXISTS analytics_daily_dimension_rollups (
        site_id INTEGER NOT NULL,
        day DATE NOT NULL,
        dimension TEXT NOT NULL,
        value TEXT NOT NULL,
        label TEXT NOT NULL,
        visitors INTEGER NOT NULL DEFAULT 0,
        visits INTEGER NOT NULL DEFAULT 0,
        pageviews INTEGER NOT NULL DEFAULT 0,
        created_at TIMESTAMP DEFAULT NOW() NOT NULL,
        updated_at TIMESTAMP DEFAULT NOW() NOT NULL,
        CONSTRAINT analytics_daily_dimension_site_day_dim_value_unique UNIQUE (site_id, day, dimension, value)
    )`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS analytics_daily_dimension_site_day_idx ON analytics_daily_dimension_rollups (site_id, day, dimension)`,
  );
  await assertPlatformAnalyticsSchemaReady(params);
}
